import { useState } from 'react';
import { Plus, X, Trash2, Pencil } from 'lucide-react';
import { adminAddPackage, adminUpdatePackage, adminDeletePackage } from '@/lib/api';
import { inp } from './styles';

const DEFAULT_MIN_GUESTS = 10;
const DEFAULT_MAX_GUESTS = 15;
const DEFAULT_BOOKING_LINK = 'https://propelsports.myesoftplanner.com';

export function PackagesPanel({ packages, onRefresh }) {
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  return (
    <div data-testid="packages-panel" className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-slate-400 text-sm">Manage birthday party and event packages. Changes appear on the Parties & Events page.</p>
        <button onClick={() => { setShowAdd(!showAdd); setEditingId(null); }} data-testid="add-package-btn"
          className="flex items-center gap-2 bg-[#F26522] hover:bg-[#D9551A] text-white px-5 py-2.5 rounded-xl font-medium text-sm transition-all">
          {showAdd ? <X size={16} /> : <Plus size={16} />}
          {showAdd ? 'Cancel' : 'Add Package'}
        </button>
      </div>

      {showAdd && <PackageForm onSuccess={() => { setShowAdd(false); onRefresh(); }} />}

      {packages.map(pkg => (
        <div key={pkg.id} data-testid={`admin-package-${pkg.id}`}>
          {editingId === pkg.id ? (
            <PackageForm pkg={pkg} onSuccess={() => { setEditingId(null); onRefresh(); }} onCancel={() => setEditingId(null)} />
          ) : (
            <div className="bg-white/[0.03] border border-white/5 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h4 className="text-white font-semibold">{pkg.name}</h4>
                  <span className="text-[#F26522] font-bold text-lg">{pkg.price}</span>
                </div>
                <div className="text-slate-400 text-xs mb-2">{pkg.duration} &middot; {pkg.min_guests}-{pkg.max_guests} guests</div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(pkg.sports_included || []).map(s => (
                    <span key={s} className="bg-[#F26522]/10 text-[#F26522] px-2 py-0.5 rounded-full text-[10px] font-medium">{s}</span>
                  ))}
                </div>
                <div className="text-slate-500 text-xs">{(pkg.whats_included || []).join(' · ')}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => { setEditingId(pkg.id); setShowAdd(false); }} data-testid={`edit-package-${pkg.id}`}
                  className="text-slate-400 hover:text-[#F26522] p-2 transition-colors"><Pencil size={16} /></button>
                <button onClick={async () => { await adminDeletePackage(pkg.id); onRefresh(); }} data-testid={`delete-package-${pkg.id}`}
                  className="text-red-400 hover:text-red-300 p-2 transition-colors"><Trash2 size={16} /></button>
              </div>
            </div>
          )}
        </div>
      ))}

      {packages.length === 0 && !showAdd && (
        <p className="text-slate-500 text-sm text-center py-8">No packages yet. Click "Add Package" to create one.</p>
      )}
    </div>
  );
}

function PackageForm({ pkg, onSuccess, onCancel }) {
  const isEdit = !!pkg;
  const [name, setName] = useState(pkg?.name || '');
  const [price, setPrice] = useState(pkg?.price || '');
  const [duration, setDuration] = useState(pkg?.duration || '');
  const [minGuests, setMinGuests] = useState(pkg?.min_guests || DEFAULT_MIN_GUESTS);
  const [maxGuests, setMaxGuests] = useState(pkg?.max_guests || DEFAULT_MAX_GUESTS);
  const [sportsIncluded, setSportsIncluded] = useState((pkg?.sports_included || []).join(', '));
  const [whatsIncluded, setWhatsIncluded] = useState((pkg?.whats_included || []).join('\n'));
  const [bookingLink, setBookingLink] = useState(pkg?.booking_link || DEFAULT_BOOKING_LINK);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setSaving(true);
    try {
      const data = { name, price, duration, min_guests: Number(minGuests), max_guests: Number(maxGuests), sports_included: sportsIncluded, whats_included: whatsIncluded, booking_link: bookingLink };
      if (isEdit) {
        await adminUpdatePackage(pkg.id, data);
      } else {
        await adminAddPackage(data);
      }
      onSuccess();
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="package-form" className="bg-white/[0.03] border border-white/5 rounded-2xl p-6 space-y-4">
      <h4 className="text-white font-semibold">{isEdit ? 'Edit Package' : 'New Package'}</h4>
      {err && <p className="text-red-400 text-sm">{err}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-slate-400 text-xs mb-1.5 font-medium">Package Name</label>
          <input value={name} onChange={e => setName(e.target.value)} required placeholder="e.g. 90-Minute Party" className={inp} data-testid="pkg-name" />
        </div>
        <div>
          <label className="block text-slate-400 text-xs mb-1.5 font-medium">Price</label>
          <input value={price} onChange={e => setPrice(e.target.value)} required placeholder="e.g. $159.99" className={inp} data-testid="pkg-price" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-slate-400 text-xs mb-1.5 font-medium">Duration</label>
          <input value={duration} onChange={e => setDuration(e.target.value)} required placeholder="e.g. 90 minutes" className={inp} data-testid="pkg-duration" />
        </div>
        <div>
          <label className="block text-slate-400 text-xs mb-1.5 font-medium">Min Guests</label>
          <input type="number" value={minGuests} onChange={e => setMinGuests(e.target.value)} className={inp} data-testid="pkg-min-guests" />
        </div>
        <div>
          <label className="block text-slate-400 text-xs mb-1.5 font-medium">Max Guests</label>
          <input type="number" value={maxGuests} onChange={e => setMaxGuests(e.target.value)} className={inp} data-testid="pkg-max-guests" />
        </div>
      </div>

      <div>
        <label className="block text-slate-400 text-xs mb-1.5 font-medium">Sports Included (comma-separated)</label>
        <input value={sportsIncluded} onChange={e => setSportsIncluded(e.target.value)} placeholder="Dodgeball, Soccer, Hockey, Basketball" className={inp} data-testid="pkg-sports" />
      </div>

      <div>
        <label className="block text-slate-400 text-xs mb-1.5 font-medium">What's Included (one item per line)</label>
        <textarea value={whatsIncluded} onChange={e => setWhatsIncluded(e.target.value)} rows={5} placeholder={"90 minutes of sports activities\nDedicated party host\nPaper goods and setup"} className={inp + " resize-none"} data-testid="pkg-whats-included" />
      </div>

      <div>
        <label className="block text-slate-400 text-xs mb-1.5 font-medium">Booking Link</label>
        <input value={bookingLink} onChange={e => setBookingLink(e.target.value)} placeholder="https://propelsports.myesoftplanner.com" className={inp} data-testid="pkg-booking-link" />
      </div>

      <div className="flex gap-3">
        <button type="submit" disabled={saving} data-testid="pkg-save-btn"
          className="bg-[#F26522] hover:bg-[#D9551A] disabled:opacity-50 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all">
          {saving ? 'Saving...' : isEdit ? 'Update Package' : 'Add Package'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="bg-white/5 text-slate-400 hover:text-white px-6 py-3 rounded-xl font-medium text-sm transition-all">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
